import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import SEO from "../components/SEO";

export default function Connexion() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/mon-carnet";

  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="connexion-page" className="bg-[#FBF8EF] min-h-screen pt-28 md:pt-40 pb-32">
      <SEO title="Connexion — Pulaar" description="Connectez-vous à votre carnet Pulaar." path="/connexion" />
      <div className="mx-auto max-w-md px-6">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">— Carnet</p>
        <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-[0.95] tracking-[-0.02em]">
          Se connecter<span className="italic font-light">.</span>
        </h1>
        <p className="mt-5 text-sm font-serif italic text-[#4A4641] leading-relaxed">
          Retrouvez votre carnet, vos adresses, vos images.
        </p>

        <button
          type="button"
          onClick={loginWithGoogle}
          data-testid="connexion-google-btn"
          className="mt-10 w-full h-12 bg-white border border-[#D8D2C5] text-[#121212] text-[11px] tracking-[0.28em] uppercase flex items-center justify-center gap-3 transition-colors duration-500 hover:bg-[#F2EDE0]"
        >
          <GoogleIcon /> Continuer avec Google
        </button>

        <div className="my-8 flex items-center gap-4">
          <div className="flex-1 h-px bg-[#D8D2C5]" />
          <span className="text-[10px] tracking-[0.32em] uppercase text-[#8C8178]">Ou</span>
          <div className="flex-1 h-px bg-[#D8D2C5]" />
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-2">Email</label>
            <input
              data-testid="connexion-input-email"
              required type="email" autoComplete="email"
              value={form.email} onChange={onChange("email")}
              className="w-full h-11 bg-transparent border-b border-[#D8D2C5] outline-none text-sm text-[#121212] focus:border-[#121212] transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-2">Mot de passe</label>
            <input
              data-testid="connexion-input-password"
              required type="password" autoComplete="current-password"
              value={form.password} onChange={onChange("password")}
              className="w-full h-11 bg-transparent border-b border-[#D8D2C5] outline-none text-sm text-[#121212] focus:border-[#121212] transition-colors"
            />
          </div>
          {error && <p className="text-xs" style={{ color: "#A33A2A" }} data-testid="connexion-error">{error}</p>}
          <button
            type="submit" disabled={loading}
            data-testid="connexion-submit"
            className="w-full h-12 text-[11px] tracking-[0.32em] uppercase bg-[#121212] text-[#FBF8EF] flex items-center justify-center gap-2 disabled:opacity-50 transition-colors duration-500 hover:bg-[#3A322C]"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <p className="mt-8 text-xs text-[#4A4641] text-center">
          Pas encore de carnet ?{" "}
          <Link to="/inscription" className="text-[#121212] link-underline" data-testid="connexion-to-inscription">
            Créer un compte
          </Link>
        </p>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92a8.78 8.78 0 002.68-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86a5.27 5.27 0 01-4.96-3.65H.96v2.33A8.99 8.99 0 009 18z"/>
      <path fill="#FBBC05" d="M4.04 10.77a5.41 5.41 0 010-3.54V4.9H.96a9 9 0 000 8.2l3.08-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.99 8.99 0 00.96 4.9l3.08 2.33A5.27 5.27 0 019 3.58z"/>
    </svg>
  );
}
